import { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { canonical, faqPageSchema } from "@/utils/seo";

const faqs = [
  {
    category: "關於平台",
    items: [
      {
        q: "美好時光指南是什麼？",
        a: "美好時光指南是台灣的美業探店評比媒體，我們派出神秘客實際體驗各類美業服務（按摩、美容、美甲、美髮、美睫、身心靈等），並撰寫真實評比文章，幫助使用者找到最適合自己的美業體驗。",
      },
      {
        q: "平台上的評比是真實的嗎？",
        a: "是的。我們的神秘客在體驗過程中不會向店家透露身份，確保獲得與一般消費者相同的服務品質。評比內容完全基於實際體驗，且會標註優點與缺點，不做片面美化。",
      },
      {
        q: "美好時光指南如何獲利？",
        a: "我們的收入來自於內容合作與品牌曝光。但請放心，我們不會因為商業合作而改變評比結果——正面評價必須建立在實際優質體驗之上。",
      },
    ],
  },
  {
    category: "使用指南",
    items: [
      {
        q: "文章裡的店家資訊準確嗎？",
        a: "我們的文章均由神秘客實地走訪後撰寫，並定期更新店家資訊。建議前往前再向店家確認最新營業時間與服務內容。",
      },
      {
        q: "文章裡的價格會過期嗎？",
        a: "我們會定期更新文章中的價格資訊，但市場價格仍可能因季節、促銷而變動。建議前往前再向店家確認最新價格。文章中會標註最後更新日期供參考。",
      },
      {
        q: "如何找到離我最近的店家？",
        a: "你可以透過「探索城市」選擇你所在的城市，或使用搜尋功能輸入地區名稱（如「東區」「信義區」）。我們也在開發地圖功能，未來將可直接在地圖上瀏覽附近店家。",
      },
    ],
  },
  {
    category: "讀者服務",
    items: [
      {
        q: "如何收藏喜歡的文章？",
        a: "你可以將喜歡的文章網址加入瀏覽器的書籤，或使用我們的收藏功能（需登入會員，即將上線）。我們也建議追蹤我們的社群媒體，不錯過任何新文章。",
      },
      {
        q: "有沒有電子報或通知？",
        a: "我們正在規劃週刊電子報，將每週精選最新探店文章與限時優惠推薦給訂閱者。電子報訂閱功能即將上線，敬請期待。",
      },
      {
        q: "如何向平台推薦店家？",
        a: "歡迎透過「聯絡我們」頁面或 email 向我們推薦你覺得值得評比的店家。我們會根據讀者需求與神祕客排程，安排實地走訪。",
      },
    ],
  },
  {
    category: "商家合作",
    items: [
      {
        q: "我的店家可以上平台嗎？",
        a: "歡迎！請前往「商家合作」頁面了解合作方式，或直接寄信至 partners@goldentimeguide.com，我們的商務團隊會與你聯繫。",
      },
      {
        q: "合作會影響評比結果嗎？",
        a: "不會。即使與我們有合作關係，神秘客仍會匿名體驗並撰寫真實評比。我們拒絕「付費換好評」的合作模式，確保平台的公信力。",
      },
    ],
  },
];

function FAQItem({ question, answer, isOpen, onToggle }: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left cursor-pointer group"
      >
        <span className="text-sm md:text-base font-medium text-slate-800 group-hover:text-primary transition-colors pr-4">
          {question}
        </span>
        <div className="w-6 h-6 flex items-center justify-center shrink-0">
          <i
            className={`ri-arrow-down-s-line text-slate-400 transition-transform duration-300 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-96 pb-4" : "max-h-0"
        }`}
      >
        <p className="text-sm text-slate-500 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export default function FAQPage() {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const allFaqItems = faqs.flatMap((g) => g.items.map((item) => ({ question: item.q, answer: item.a })));

  return (
    <div className="w-full">
      <SEO
        meta={{
          title: "常見問題｜美好時光指南",
          description: "關於美好時光指南的常見問題，包括平台介紹、使用方式、會員帳號與商家合作等。",
          canonical: canonical("/faq"),
          keywords: ["常見問題", "FAQ", "平台介紹", "使用方式", "美好時光指南"],
          structuredData: faqPageSchema(allFaqItems),
        }}
      />

      {/* Header */}
      <div className="w-full bg-slate-50 border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-16 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            常見問題
          </h1>
          <p className="text-slate-500">
            找不到答案？請寄信至 contact@goldentimeguide.com，我們會盡快回覆你。
          </p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div
          className={`space-y-10 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          {faqs.map((group) => (
            <div key={group.category}>
              <h2 className="text-lg font-bold text-slate-800 mb-4">
                {group.category}
              </h2>
              <div className="bg-white rounded-xl border border-slate-100 px-5">
                {group.items.map((item, idx) => {
                  const key = `${group.category}-${idx}`;
                  return (
                    <FAQItem
                      key={key}
                      question={item.q}
                      answer={item.a}
                      isOpen={openKey === key}
                      onToggle={() =>
                        setOpenKey(openKey === key ? null : key)
                      }
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-400 mb-3">
            還有其他問題？
          </p>
          <a
            href="mailto:contact@goldentimeguide.com"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-full hover:border-primary/30 hover:text-primary transition-colors whitespace-nowrap"
          >
            <i className="ri-mail-line" />
            聯絡我們
          </a>
        </div>
      </div>
    </div>
  );
}